import React, { useRef } from 'react';
import Heading from '../../../components/typography';
import { LogoUploadContainer, LogoErrorMessage } from './whiteLabel.styled';
import Spinner from '../../../components/spinner';

interface LogoUploadProps {
  logoPreview: {
    content: string | null;
    name: string | null;
  };
  onLogoChange: (file: File) => void;
  onDeleteClick: () => void;
  errors?: any;
  touched?: any;
  isLoading?: boolean;
  disabled?: boolean;
  id?: string;
}

const LogoUpload = ({
  logoPreview,
  onLogoChange,
  onDeleteClick,
  errors,
  touched,
  isLoading = false,
  disabled = false,
  id,
}: LogoUploadProps): JSX.Element => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (file) {
      onLogoChange(file);
    }
    // Reset input value to allow re-uploading the same file
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const hasError = Boolean(errors?.logo) && Boolean(touched?.logo);

  // Check if content is a URL, data URL, or base64
  const getImageSrc = (): string | null => {
    if (!logoPreview?.content) return null;
    
    const content = logoPreview.content;
    // Already a data URL
    if (content.startsWith('data:')) return content;
    // HTTP/HTTPS URL
    if (content.startsWith('http://') || content.startsWith('https://')) return content;
    // Relative URL (starts with /)
    if (content.startsWith('/')) return content;
    // Assume it's base64
    return `data:image/png;base64,${content}`;
  };
  
  const imageSrc = getImageSrc();

  return (
    <div id={id}>
      <LogoUploadContainer id="logoUploadContainer" className={hasError ? 'has-error' : ''}>
        <div className={`logo-preview ${hasError ? 'is-invalid' : ''}`} id="logoPreview">
          {isLoading ? (
            <div className='loading-container' id="logoLoadingContainer">
              <Spinner />
            </div>
          ) : (
            <>
              {!disabled && (
                <label className='upload-label' role='button' id="logoUploadLabel">
                  <span className='material-symbols-outlined icon' id="logoUploadIcon">add_a_photo</span>
                  <Heading variant='body-xs' title='Change' className='upload-text' id="logoUploadText" />
                  <input
                    ref={fileInputRef}
                    type='file'
                    accept='.jpg, .png, .jpeg'
                    onChange={handleFileChange}
                    id="logoFileInput"
                  />
                </label>
              )}
              {!disabled && logoPreview?.content && (
                <label
                  className='delete-icon'
                  role='button'
                  onClick={onDeleteClick}
                  id="logoDeleteLabel"
                >
                  <span className='material-symbols-outlined icon' id="logoDeleteIcon">delete</span>
                </label>
              )}
              {imageSrc ? (
                <img
                  src={imageSrc}
                  alt={logoPreview.name ?? 'Company Logo'}
                  id="logoImage"
                />
              ) : (
                <div className='default-logo' id="defaultLogoContainer">
                  <span className='material-symbols-outlined placeholder-icon' id="placeholderIcon">image</span>
                  <Heading variant='body-xs' title='No logo uploaded' className='placeholder-text' id="placeholderText" />
                </div>
              )}
            </>
          )}
        </div>
        {/* {logoPreview?.name && !isLoading && (
          <Heading
            variant='body-xs'
            title={logoPreview.name}
            className='logo-name'
            id="logoFileName"
          />
        )} */}
      </LogoUploadContainer>

      {hasError && (
        <LogoErrorMessage id="logoErrorMessage">
          {errors.logo}
        </LogoErrorMessage>
      )}
    </div>
  );
};

export default LogoUpload;
